import { useId, useMemo, useRef, useState } from 'react';
import { Check, CircleAlert, CircleCheck, Copy, ExternalLink, MapPinOff, Minus, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Segmented } from '@/components/ui/segmented';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SidePanel, SidePanelSection } from '@/components/ui/side-panel';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Avatar } from '@/components/ui/avatar-initials';
import ProjectTagsInput from '@/components/ProjectTagsInput';
import {
  CITY_OPTIONS,
  cleanText,
  createProjectCustomField,
  formChanged,
  isProjectNameTakenMessage,
  joinLocation,
  nameTakenBy,
  normalizeProjectCustomFields,
  normalizeProjectPayload,
  OTHER_SYSTEM,
  PROJECT_STATUS_OPTIONS,
  projectHref,
  projectNameKey,
  projectsNamed,
  scopeOptions,
  splitLocation,
  SYSTEM_TYPE_CHOICES,
  systemChoiceOf,
  systemProjectName,
  validateProjectForm,
  wazeLinkStatus,
} from './projectsModel';

/*
  New / Edit / Duplicate project — the right-hand drawer (03 #8) on the foundation SidePanel: sections
  (Basics · Client & location · Team · Dates · More), the system type as chips, a live Waze pin check, a
  date-order check, and the unsaved-changes guard (×, Cancel, Esc and a click outside ask before discarding).
  Project names are unique (BUG-009, owner decision 2026-10-04): a name another project has is a blocking error
  on the name field — with what uses it, "Open …" (new tab), "Prefill from …" (New) and, for the same site's other
  system, "Use “<name> — <system>”" — and the server's own refusal shows on the field too.
*/

const NONE = '__none__';
const TITLES = {
  create: 'New project',
  edit: 'Edit project details',
  duplicate: 'Duplicate for another system',
};
const LABEL = 'text-[12px] font-semibold text-ink-2';
const NAME_ACTION = 'rounded font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function Field({ label, htmlFor, hint, error, errorId, errorTestId, children, className }) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      {label ? (
        <label htmlFor={htmlFor} className={cn(LABEL, 'flex items-center gap-1.5')}>
          {label}
          {hint ? <span className="ml-auto text-[11.5px] font-medium text-ink-3">{hint}</span> : null}
        </label>
      ) : null}
      {children}
      {error ? (
        <p id={errorId} className="flex items-center gap-1 text-[12px] font-semibold text-status-red-ink" role="alert" data-testid={errorTestId}>
          <CircleAlert className="h-[13px] w-[13px] shrink-0" aria-hidden="true" />
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Text field with suggestions from the values already in use (Client, Consultant, Contractor). */
function SuggestionInput({ id, value, onChange, options, placeholder, testId, describedBy }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const listId = useId();
  const query = cleanText(value).toLowerCase();
  const filtered = options
    .filter((item) => item.toLowerCase() !== query && (!query || item.toLowerCase().includes(query)))
    .slice(0, 8);
  const showList = open && filtered.length > 0;
  const pick = (item) => {
    onChange(item);
    setOpen(false);
    setActive(-1);
  };
  return (
    <div className="relative">
      <Input
        id={id}
        value={value}
        onChange={(event) => { onChange(event.target.value); setOpen(true); setActive(-1); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={(event) => {
          if (!showList) return;
          if (event.key === 'ArrowDown') { event.preventDefault(); setActive((index) => Math.min(filtered.length - 1, index + 1)); }
          if (event.key === 'ArrowUp') { event.preventDefault(); setActive((index) => Math.max(-1, index - 1)); }
          if (event.key === 'Enter' && active >= 0) { event.preventDefault(); pick(filtered[active]); }
          if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setOpen(false); }
        }}
        placeholder={placeholder}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={showList ? listId : undefined}
        aria-activedescendant={showList && active >= 0 ? `${listId}-${active}` : undefined}
        aria-describedby={describedBy}
        autoComplete="off"
        className="h-9"
        data-testid={testId}
      />
      {showList ? (
        <div id={listId} role="listbox" className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-xl border border-line bg-surface p-1 shadow-overlay">
          {filtered.map((item, index) => (
            <button
              key={item}
              id={`${listId}-${index}`}
              type="button"
              role="option"
              aria-selected={index === active}
              tabIndex={-1}
              onMouseDown={(event) => { event.preventDefault(); pick(item); }}
              className={cn('block w-full truncate rounded-lg px-2.5 py-1.5 text-left text-[13px] text-ink hover:bg-surface-hover', index === active && 'bg-surface-hover')}
            >
              {item}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** System type as radio chips: BMS · LCS · HA KNX · HA ZigB · EMS · GRMS · Other (+ its own text). */
function SystemTypeChips({ value, onChange, invalid, describedBy, labelledBy }) {
  const choice = systemChoiceOf(value);
  const [otherOpen, setOtherOpen] = useState(choice === OTHER_SYSTEM);
  const groupRef = useRef(null);
  const options = [...SYSTEM_TYPE_CHOICES, OTHER_SYSTEM];
  const selected = otherOpen ? OTHER_SYSTEM : choice;
  const select = (option) => {
    if (option === OTHER_SYSTEM) {
      setOtherOpen(true);
      if (choice !== OTHER_SYSTEM) onChange('');
      setTimeout(() => groupRef.current?.parentElement?.querySelector('[data-other-input]')?.focus(), 0);
      return;
    }
    setOtherOpen(false);
    onChange(option);
  };
  const onKeyDown = (event, index) => {
    if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(event.key)) return;
    event.preventDefault();
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    const next = (index + step + options.length) % options.length;
    groupRef.current?.querySelectorAll('[role="radio"]')[next]?.focus();
    select(options[next]);
  };
  return (
    <>
      <div
        ref={groupRef}
        role="radiogroup"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        className="flex flex-wrap gap-1"
        data-testid="project-system-type-input"
        data-value={cleanText(value)}
      >
        {options.map((option, index) => {
          const checked = selected === option;
          const focusable = selected ? checked : index === 0;
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={focusable ? 0 : -1}
              onClick={() => select(option)}
              onKeyDown={(event) => onKeyDown(event, index)}
              className={cn(
                'inline-flex h-[30px] items-center gap-1 rounded-full border px-[9px] text-[12.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                checked
                  ? 'border-[color:var(--brand-soft-border)] bg-brand-soft text-brand-ink'
                  : cn('bg-surface text-ink-2 hover:bg-surface-hover', invalid ? 'border-status-red/60' : 'border-line')
              )}
            >
              {checked ? <Check className="h-3 w-3" aria-hidden="true" /> : null}
              {option === OTHER_SYSTEM ? 'Other' : option}
            </button>
          );
        })}
      </div>
      {otherOpen ? (
        <Input
          data-other-input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Name the system, e.g. HA Hyb"
          aria-label="Other system type"
          className="h-9"
        />
      ) : null}
    </>
  );
}

function PersonSelect({ id, value, onChange, people, testId, label }) {
  const names = useMemo(() => {
    const list = [...people];
    if (cleanText(value) && !list.includes(cleanText(value))) list.push(cleanText(value));
    return list;
  }, [people, value]);
  return (
    <Select value={cleanText(value) || NONE} onValueChange={(next) => onChange(next === NONE ? '' : next)}>
      <SelectTrigger id={id} className="h-9" data-testid={testId} aria-label={label}>
        <SelectValue placeholder="Unassigned" />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        <SelectItem value={NONE}><span className="text-ink-3">Unassigned</span></SelectItem>
        {names.map((name) => (
          <SelectItem key={name} value={name}>
            <span className="flex items-center gap-2"><Avatar name={name} size={18} />{name}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const PREFILL_FIELDS = [
  'client', 'consultant', 'contractor', 'location', 'waze_url', 'project_engineer', 'senior_project_engineer',
  'scope', 'start_date', 'end_date',
];
// The richest same-name project is the best one to copy from.
const filledCount = (project) => PREFILL_FIELDS.filter((field) => (Array.isArray(project[field]) ? project[field].length : cleanText(project[field]))).length;

export default function ProjectFormPanel({
  open,
  onOpenChange,
  mode = 'create',
  sourceName = '',
  projectId = null,
  initialForm,
  onSubmit,
  projects = [],
  options,
}) {
  const ids = useId();
  const fieldId = (name) => `${ids}-${name}`;
  const [initial] = useState(initialForm);
  const [form, setForm] = useState(initialForm);
  const [showErrors, setShowErrors] = useState(false);
  const startPlace = useMemo(() => splitLocation(initialForm.location), [initialForm.location]);
  const [city, setCity] = useState(startPlace.city);
  const [area, setArea] = useState(startPlace.area);
  const {
    clients = [], clientCounts = {}, consultants = [], contractors = [], tags = [], engineers = [],
  } = options || {};

  const [saveError, setSaveError] = useState('');
  const set = (patch) => {
    setSaveError('');
    setForm((previous) => ({ ...previous, ...patch }));
  };
  const setPlace = (nextArea, nextCity) => {
    setArea(nextArea);
    setCity(nextCity);
    set({ location: joinLocation(nextArea, nextCity) });
  };

  const errors = validateProjectForm(form, { mode, initial, projects, projectId });
  // BUG-009: a name another project has blocks saving (the server refuses it too). Shown at once, with what
  // uses it and the ways out: open that project, prefill from it (New), or a name for this system.
  const sameName = nameTakenBy(projects, form, { mode, initial, projectId });
  const nameTaken = sameName.length > 0;
  // The server's refusal (a project created since this list loaded) stays on the field until the name changes.
  const [serverName, setServerName] = useState(null);
  const serverNameError = serverName && serverName.key === projectNameKey(form.name) ? serverName.message : '';
  // The name / system type errors wait for a first save; a taken name, a wrong date order or link shows at once.
  const shown = {
    name: nameTaken ? undefined : serverNameError || (showErrors ? errors.name : undefined),
    system_type: showErrors ? errors.system_type : undefined,
    end_date: errors.end_date,
    waze_url: errors.waze_url,
  };
  const nameInvalid = nameTaken || Boolean(shown.name);
  const nameDescribedBy = nameTaken ? fieldId('name-taken') : shown.name ? fieldId('name-error') : undefined;
  const dirty = formChanged(form, initial);

  const prefillSource = mode === 'create' && nameTaken
    ? [...sameName].sort((a, b) => filledCount(b) - filledCount(a))[0]
    : null;
  const sameNameClients = [...new Set(sameName.map((p) => cleanText(p.client)).filter(Boolean))];
  const openTarget = nameTaken && mode !== 'duplicate' ? sameName[0] : null;
  const suggestedName = nameTaken && mode !== 'edit' ? systemProjectName(form.name, form.system_type) : '';
  const suggestion = suggestedName && !projectsNamed(projects, suggestedName).length ? suggestedName : '';

  const prefill = () => {
    if (!prefillSource) return;
    const patch = {};
    PREFILL_FIELDS.forEach((field) => {
      const value = prefillSource[field];
      patch[field] = Array.isArray(value) ? [...value] : (value || '');
    });
    set(patch);
    const place = splitLocation(patch.location);
    setCity(place.city);
    setArea(place.area);
  };

  const scopeChoices = useMemo(() => scopeOptions(projects, form.scope), [projects, form.scope]);
  const toggleScope = (item) => set({
    scope: form.scope.includes(item) ? form.scope.filter((value) => value !== item) : [...form.scope, item],
  });
  const customFields = normalizeProjectCustomFields(form.custom_fields);
  const updateCustomField = (id, key, value) => set({
    custom_fields: customFields.map((field) => (field.id === id ? { ...field, [key]: value } : field)),
  });
  const waze = wazeLinkStatus(form.waze_url);
  const moreCount = [form.consultant, form.contractor, form.notes].filter(cleanText).length
    + form.scope.length + form.tags.length + customFields.length + (form.allow_sub_boq ? 1 : 0);
  const cityChoices = city && !CITY_OPTIONS.includes(city) ? [...CITY_OPTIONS, city] : CITY_OPTIONS;

  // The footer's save error (SidePanel `error`, controlled here) describes the form as it was saved: the next edit
  // clears it, so a fixed name does not keep "already exists" under the Save button.
  const save = async () => {
    setSaveError('');
    try {
      await attemptSave();
    } catch (err) {
      setSaveError(getErrorMessage(err, 'Could not save. Try again.'));
      throw err;
    }
  };
  const attemptSave = async () => {
    if (Object.keys(errors).length) {
      setShowErrors(true);
      const first = ['name', 'system_type', 'waze_url', 'end_date'].find((key) => errors[key]);
      setTimeout(() => {
        const target = document.getElementById(fieldId(first === 'system_type' ? 'system-label' : first));
        const focusable = first === 'system_type'
          ? target?.parentElement?.querySelector('[role="radio"][tabindex="0"]')
          : target;
        focusable?.focus?.();
      }, 0);
      throw new Error(Object.keys(errors).length === 1 ? Object.values(errors)[0] : 'Check the highlighted fields');
    }
    try {
      await onSubmit(normalizeProjectPayload(form));
    } catch (err) {
      // The server's word on a taken name goes on the name field as well as in the footer.
      const message = getErrorMessage(err, '');
      if (isProjectNameTakenMessage(message)) {
        setServerName({ key: projectNameKey(form.name), message });
        setTimeout(() => document.getElementById(fieldId('name'))?.focus?.(), 0);
      }
      throw err;
    }
  };

  const subtitle = mode === 'duplicate'
    ? `Same site as “${sourceName}” — pick the other system type and give it its own name.`
    : mode === 'create'
      ? 'Creates the 10 delivery stages from the default template'
      : undefined;

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      width="md"
      title={TITLES[mode] || TITLES.create}
      subtitle={subtitle}
      dirty={dirty}
      onSave={save}
      error={saveError}
      saveLabel={mode === 'edit' ? 'Save changes' : 'Create project'}
      confirmOptions={{ title: 'Discard your changes?', description: 'The project details you entered are not saved yet.', confirmLabel: 'Discard', cancelLabel: 'Keep editing' }}
      testId="project-form"
    >
      <SidePanelSection title="Basics">
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
          <Field label="Project name *" htmlFor={fieldId('name')} error={shown.name} errorId={fieldId('name-error')} errorTestId="project-name-error">
            <Input
              id={fieldId('name')}
              value={form.name}
              onChange={(event) => set({ name: event.target.value })}
              aria-invalid={nameInvalid || undefined}
              aria-describedby={nameDescribedBy}
              aria-required="true"
              placeholder="e.g. Nobu Hotel"
              className={cn('h-9', nameInvalid && 'border-status-red focus-visible:ring-status-red/40')}
              data-testid="project-name-input"
              autoFocus={mode === 'create'}
            />
          </Field>
          <Field label="Status">
            <Segmented
              ariaLabel="Status"
              size="md"
              value={PROJECT_STATUS_OPTIONS.find((option) => option.value.toLowerCase() === cleanText(form.status).toLowerCase())?.value || form.status}
              onChange={(status) => set({ status })}
              options={PROJECT_STATUS_OPTIONS.map((option) => ({ key: option.value, label: option.label, testId: `project-status-${option.value.toLowerCase().replace(/\s+/g, '-')}` }))}
            />
          </Field>
        </div>
        {nameTaken ? (
          <div
            id={fieldId('name-taken')}
            role="alert"
            className="-mt-1 flex items-start gap-2 rounded-xl border border-status-red/30 bg-status-red-bg px-3 py-2.5 text-[12.5px] leading-snug text-ink-2"
            data-testid="project-same-name"
          >
            <CircleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-red-ink" aria-hidden="true" />
            <div className="min-w-0 space-y-1">
              <p className="font-semibold text-status-red-ink" data-testid="project-name-taken">{errors.name}</p>
              <p>
                {sameName.length === 1 ? '1 project already uses' : `${formatNumber(sameName.length, { maxDecimals: 0 })} projects already use`} this name
                {' '}({[sameName.map((p) => cleanText(p.system_type) || 'no system').join(', '), sameNameClients.join(', ')].filter(Boolean).join(' · ')}).
                {mode === 'duplicate' && !cleanText(form.system_type) ? ' Pick the system type to get a name for it.' : null}
              </p>
              {openTarget || prefillSource || suggestion ? (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  {suggestion ? (
                    <button type="button" onClick={() => set({ name: suggestion })} className={NAME_ACTION} data-testid="project-name-suggestion">
                      Use “{suggestion}”
                    </button>
                  ) : null}
                  {openTarget ? (
                    <a href={projectHref(openTarget)} target="_blank" rel="noopener noreferrer" className={cn(NAME_ACTION, 'inline-flex items-center gap-1')} data-testid="project-same-name-open">
                      Open {cleanText(openTarget.name)}{openTarget.system_type ? ` · ${cleanText(openTarget.system_type)}` : ''}
                      <ExternalLink className="h-3 w-3" aria-hidden="true" />
                      <span className="sr-only">(opens in a new tab)</span>
                    </a>
                  ) : null}
                  {prefillSource ? (
                    <button type="button" onClick={prefill} className={NAME_ACTION} data-testid="project-prefill">
                      <Copy className="mr-1 inline h-3 w-3 align-[-1px]" aria-hidden="true" />
                      Prefill from {cleanText(prefillSource.name)}{prefillSource.system_type ? ` · ${cleanText(prefillSource.system_type)}` : ''}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        <Field label={null} error={shown.system_type} errorId={fieldId('system-error')}>
          <span id={fieldId('system-label')} className={LABEL}>System type{mode === 'edit' ? '' : ' *'}</span>
          <SystemTypeChips
            value={form.system_type}
            onChange={(systemType) => set({ system_type: systemType })}
            invalid={Boolean(shown.system_type)}
            labelledBy={fieldId('system-label')}
            describedBy={shown.system_type ? fieldId('system-error') : undefined}
          />
        </Field>
      </SidePanelSection>

      <SidePanelSection title="Client & location">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Client"
            htmlFor={fieldId('client')}
            hint={clientCounts[cleanText(form.client).toLowerCase()] ? `${clientCounts[cleanText(form.client).toLowerCase()]} projects` : undefined}
          >
            <SuggestionInput
              id={fieldId('client')}
              value={form.client}
              onChange={(client) => set({ client })}
              options={clients}
              placeholder="Client"
              testId="project-client-input"
            />
          </Field>
          <Field label="City" htmlFor={fieldId('city')}>
            <Select value={city || NONE} onValueChange={(next) => setPlace(area, next === NONE ? '' : next)}>
              <SelectTrigger id={fieldId('city')} className="h-9" data-testid="project-city-select">
                <SelectValue placeholder="Not set" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}><span className="text-ink-3">Not set</span></SelectItem>
                {cityChoices.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Area / address" htmlFor={fieldId('area')}>
          <Input
            id={fieldId('area')}
            value={area}
            onChange={(event) => setPlace(event.target.value, city)}
            placeholder="District, street or building"
            className="h-9"
            data-testid="project-location-input"
          />
        </Field>
        <Field
          label="Waze link"
          htmlFor={fieldId('waze_url')}
          error={shown.waze_url}
          errorId={fieldId('waze-error')}
          hint={waze && waze.tone !== 'red' ? (
            <span className={cn('inline-flex items-center gap-1 font-semibold', waze.tone === 'green' ? 'text-status-green-ink' : 'text-status-amber-ink')} data-testid="project-waze-status">
              {waze.tone === 'green' ? <CircleCheck className="h-[13px] w-[13px]" aria-hidden="true" /> : <MapPinOff className="h-[13px] w-[13px]" aria-hidden="true" />}
              {waze.tone === 'green' ? waze.text : 'No map pin in this link'}
            </span>
          ) : undefined}
        >
          <Input
            id={fieldId('waze_url')}
            type="url"
            value={form.waze_url}
            onChange={(event) => set({ waze_url: event.target.value })}
            placeholder="https://waze.com/ul?ll=25.2048,55.2708&navigate=yes"
            aria-invalid={Boolean(shown.waze_url) || undefined}
            aria-describedby={shown.waze_url ? fieldId('waze-error') : undefined}
            className={cn('h-9', shown.waze_url && 'border-status-red')}
            data-testid="project-waze-url-input"
          />
          {waze?.tone === 'amber' ? <p className="text-[12px] text-ink-3">{waze.text}.</p> : null}
        </Field>
      </SidePanelSection>

      <SidePanelSection title="Team">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Project Engineer" htmlFor={fieldId('pe')}>
            <PersonSelect id={fieldId('pe')} value={form.project_engineer} onChange={(value) => set({ project_engineer: value })} people={engineers} testId="project-engineer-select" label="Project Engineer" />
          </Field>
          <Field label="Senior Project Engineer" htmlFor={fieldId('spe')}>
            <PersonSelect id={fieldId('spe')} value={form.senior_project_engineer} onChange={(value) => set({ senior_project_engineer: value })} people={engineers} testId="project-senior-engineer-select" label="Senior Project Engineer" />
          </Field>
        </div>
      </SidePanelSection>

      <SidePanelSection title="Dates">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Start date" htmlFor={fieldId('start_date')}>
            <Input id={fieldId('start_date')} type="date" value={form.start_date || ''} onChange={(event) => set({ start_date: event.target.value })} className="h-9" data-testid="project-start-date-input" />
          </Field>
          <Field label="End date" htmlFor={fieldId('end_date')}>
            <Input
              id={fieldId('end_date')}
              type="date"
              value={form.end_date || ''}
              onChange={(event) => set({ end_date: event.target.value })}
              aria-invalid={Boolean(shown.end_date) || undefined}
              aria-describedby={shown.end_date ? fieldId('end-error') : undefined}
              className={cn('h-9', shown.end_date && 'border-status-red')}
              data-testid="project-end-date-input"
            />
          </Field>
        </div>
        {shown.end_date ? (
          <p id={fieldId('end-error')} className="-mt-1 flex items-center gap-1 text-[12px] font-semibold text-status-red-ink" role="alert" data-testid="project-date-error">
            <CircleAlert className="h-[13px] w-[13px] shrink-0" aria-hidden="true" />
            {shown.end_date}
          </p>
        ) : null}
      </SidePanelSection>

      <SidePanelSection
        title={moreCount ? `More details · ${moreCount}` : 'More details'}
        description="Consultant, contractor, scope, tags, notes, extra fields, sub BOQ"
        collapsible
        defaultOpen={mode !== 'create' && moreCount > 0}
        className="border-t border-line-2 pt-4"
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Consultant" htmlFor={fieldId('consultant')}>
            <SuggestionInput id={fieldId('consultant')} value={form.consultant} onChange={(consultant) => set({ consultant })} options={consultants} placeholder="Consultant" />
          </Field>
          <Field label="Contractor / GC" htmlFor={fieldId('contractor')}>
            <SuggestionInput id={fieldId('contractor')} value={form.contractor} onChange={(contractor) => set({ contractor })} options={contractors} placeholder="Contractor / GC" />
          </Field>
        </div>
        <Field label="Scope">
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Scope">
            {scopeChoices.map((item) => {
              const on = form.scope.includes(item);
              return (
                <button
                  key={item}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleScope(item)}
                  className={cn(
                    'inline-flex h-8 items-center gap-1 rounded-full border px-3 text-[12.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    on ? 'border-[color:var(--brand-soft-border)] bg-brand-soft text-brand-ink' : 'border-line bg-surface text-ink-2 hover:bg-surface-hover'
                  )}
                >
                  {on ? <Check className="h-3 w-3" aria-hidden="true" /> : null}
                  {item}
                </button>
              );
            })}
          </div>
        </Field>
        <ProjectTagsInput tags={form.tags} suggestions={tags} onChange={(nextTags) => set({ tags: nextTags })} />
        <Field label="Notes" htmlFor={fieldId('notes')}>
          <Textarea id={fieldId('notes')} value={form.notes} onChange={(event) => set({ notes: event.target.value })} rows={3} />
        </Field>
        <Field
          label="Extra fields"
          hint={(
            <Button type="button" variant="ghost" size="xs" onClick={() => set({ custom_fields: [...customFields, createProjectCustomField()] })}>
              <Plus aria-hidden="true" />Add field
            </Button>
          )}
        >
          {customFields.length ? customFields.map((field) => (
            <div key={field.id} className="grid grid-cols-[minmax(0,0.45fr)_minmax(0,1fr)_auto] gap-2">
              <Input value={field.title} onChange={(event) => updateCustomField(field.id, 'title', event.target.value)} placeholder="Field title" aria-label="Field title" className="h-9" />
              <Input value={field.content} onChange={(event) => updateCustomField(field.id, 'content', event.target.value)} placeholder="Field content" aria-label="Field content" className="h-9" />
              <Button
                type="button"
                size="icon-sm"
                variant="ghost"
                onClick={() => set({ custom_fields: customFields.filter((item) => item.id !== field.id) })}
                aria-label={`Remove ${field.title || 'this field'}`}
                className="text-ink-3"
              >
                <Minus aria-hidden="true" />
              </Button>
            </div>
          )) : <p className="text-[12.5px] text-ink-3">None yet.</p>}
        </Field>
        <label className="flex items-center gap-2.5 text-[13px] text-ink-2">
          <Switch checked={Boolean(form.allow_sub_boq)} onCheckedChange={(checked) => set({ allow_sub_boq: Boolean(checked) })} aria-label="Allow sub BOQ" />
          <span><b className="font-semibold text-ink">Allow sub BOQ</b> — group the BOQ into sub BOQs on this project</span>
        </label>
      </SidePanelSection>
    </SidePanel>
  );
}
